import { action, makeObservable, observable, reaction } from 'mobx';
import {
  DEFAULT_MULTIPLIER_LIMITS,
  DEFAULT_RANGE_VALUE,
  DICE_MULTIPLIER_PRECISION,
  DICE_MULTIPLIER_STEP_VALUE,
  DICE_RANGE_LIMIT_OPTIONS,
  DICE_WIN_CHANCE_PRECISION,
  DICE_WIN_CHANCE_STEP_VALUE,
  DiceSoundsName,
  RANGE_TYPE,
} from '../diceCore/constants';
import {
  getMultiplierLimitsByRtp,
  getOddByBetType,
  getRangeNumberCountFromMultiplier,
  getWinChanceLimitsByRtp,
} from '../diceCore/helpers';
import {
  parseNumberByPrecision,
  parseNumberPrecisionByDoteCount,
} from '../../../core/helpers/parseNumberByPrecision';
import { userInfoStore } from '../../../store/userInfoStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { isMobile } from '../../../core/helpers/isMobile';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { mathConfig, mathJS } from '../../../core/helpers/mathFormat';
import { freeBetStore } from '../../../store/freeBetStore';

class DiceBetsStore {
  isBetsClosed = false;

  rangeValue = DEFAULT_RANGE_VALUE;

  rangeCurrentValue = null;

  rangeType = RANGE_TYPE.Under;

  multiplier = getOddByBetType[RANGE_TYPE.Under](DEFAULT_RANGE_VALUE, userInfoStore.rtp);

  winChance = parseNumberByPrecision(
    100,
    userInfoStore.rtp / +this.multiplier,
    DICE_WIN_CHANCE_PRECISION
  );

  winChanceLimit = {
    minBet: 1,
    maxBet: 96.029702,
  };

  multiplierLimit = DEFAULT_MULTIPLIER_LIMITS;

  payout = this.multiplier * userInfoStore.betAmount;

  roll = DEFAULT_RANGE_VALUE;

  gameResult = null;

  lastResults = [];

  maxWinLimit = null;

  isWin = null;

  isLastRoundWin = null;

  isShowResultLine = true;

  isAnimationEnd = false;

  diceRollLimitByBetType = {
    [RANGE_TYPE.Over]: {
      maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
        this.multiplierLimit.maxBet,
        userInfoStore.rtp
      ),
      minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
        this.multiplierLimit.minBet,
        userInfoStore.rtp
      ),
    },
    [RANGE_TYPE.Under]: {
      maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
        this.multiplierLimit.minBet,
        userInfoStore.rtp
      ),
      minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
        this.multiplierLimit.maxBet,
        userInfoStore.rtp
      ),
    },
  };

  winAmount = null;

  winOdd = null;

  constructor() {
    makeObservable(this, {
      roll: observable,
      isWin: observable,
      winOdd: observable,
      payout: observable,
      winAmount: observable,
      winChance: observable,
      maxWinLimit: observable,
      rangeType: observable,
      rangeValue: observable,
      isLastRoundWin: observable,
      multiplier: observable,
      isShowResultLine: observable,
      gameResult: observable,
      isBetsClosed: observable,
      isAnimationEnd: observable,
      lastResults: observable.deep,
      diceRollLimitByBetType: observable.deep,
      rangeCurrentValue: observable,
      setRoll: action.bound,
      setIsWin: action.bound,
      setRangeType: action.bound,
      setWinChance: action.bound,
      setWinAmount: action.bound,
      setWinOdd: action.bound,
      setRangeValue: action.bound,
      setGameResult: action.bound,
      setMultiplier: action.bound,
      checkValidOdd: action.bound,
      setMaxWinLimit: action.bound,
      setLastResults: action.bound,
      setIsBetsClosed: action.bound,
      updateRangeType: action.bound,
      setIsAnimationEnd: action.bound,
      setIsLastRoundWin: action.bound,
      updateLastResults: action.bound,
      setWinChanceLimit: action.bound,
      setMultiplierLimit: action.bound,
      setIsShowResultLine: action.bound,
      countOddByRangeValue: action.bound,
      setRangeCurrentValue: action.bound,
      detectRangeValueByOdd: action.bound,
      handleIncreaseWinChance: action.bound,
      handleIncreaseMultiplier: action.bound,
    });

    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
      }
    );

    reaction(
      () => userInfoStore.betAmount,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.updatePayout(this.multiplier, value);
        }
      }
    );
    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => this.multiplier,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.updatePayout(this.multiplier, userInfoStore.betAmount);
          this.setIsShowResultLine(false);
        }
      }
    );
    reaction(
      () => userInfoStore.margin,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.setMultiplierLimit(getMultiplierLimitsByRtp(userInfoStore.rtp));
          this.setWinChanceLimit(getWinChanceLimitsByRtp(this.multiplierLimit));
          this.setMultiplier(
            getOddByBetType[RANGE_TYPE.Under](DEFAULT_RANGE_VALUE, userInfoStore.rtp)
          );
          this.diceRollLimitByBetType = {
            [RANGE_TYPE.Over]: {
              maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
                this.multiplierLimit.maxBet,
                userInfoStore.rtp
              ),
              minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
                this.multiplierLimit.minBet,
                userInfoStore.rtp
              ),
            },
            [RANGE_TYPE.Under]: {
              maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
                this.multiplierLimit.minBet,
                userInfoStore.rtp
              ),
              minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
                this.multiplierLimit.maxBet,
                userInfoStore.rtp
              ),
            },
          };
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prev) => {
        if (value && value.minOdd && (!prev || prev.id !== value.id)) {
          this.setMultiplier(value.minOdd);
          this.countOddByRangeValue(this.roll, true);
          this.setMultiplierLimit({
            minBet: this.multiplier,
          });
        } else if (!value) {
          this.setMultiplierLimit(DEFAULT_MULTIPLIER_LIMITS);
        }
      }
    );
  }

  setWinAmount(amount) {
    this.winAmount = amount;
    setTimeout(() => {
      this.winAmount = null;
    }, 1500);
  }

  setWinOdd(odd) {
    this.winOdd = odd;
    clearTimeout(this.timeOutOdd);
    this.timeOutOdd = setTimeout(() => {
      this.winOdd = null;
    }, 1500);
  }

  setMaxWinLimit(limit) {
    this.maxWinLimit = limit;
  }

  setIsShowResultLine(bool) {
    this.isShowResultLine = bool;
  }

  setIsLastRoundWin(bool) {
    this.isLastRoundWin = bool;
  }

  setWinChanceLimit(limit) {
    this.winChanceLimit = limit;
  }

  setMultiplierLimit(limit) {
    this.multiplierLimit = { ...this.multiplierLimit, ...limit };
  }

  setIsAnimationEnd(bool) {
    this.isAnimationEnd = bool;
  }

  setIsWin(bool) {
    this.isWin = bool;
  }

  setLastResults(lastResults) {
    this.lastResults = lastResults.map((item, index) => ({ ...item, id: index }));
  }

  updateLastResults(lastResult) {
    const updatedLastResult = [...this.lastResults, lastResult];
    if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
      updatedLastResult.splice(0, 1);
    }
    const lastEl = updatedLastResult.pop();
    this.setGameResult(lastResult.resultNumber);
    this.setLastResults(updatedLastResult);
    setTimeout(() => {
      this.setLastResults([...updatedLastResult, lastEl]);
    }, 0);
  }

  setGameResult(result) {
    this.gameResult = result;
    this.setIsShowResultLine(true);
  }

  setRoll(rangeNumber) {
    this.roll = rangeNumber;
    this.rangeValue = Math.floor(rangeNumber);
    this.setRangeCurrentValue(Math.floor(rangeNumber));
    setTimeout(() => {
      this.setRangeCurrentValue(null);
    }, 200);
    this.countOddByRangeValue(rangeNumber);
  }

  setRangeCurrentValue(value) {
    this.rangeCurrentValue = value;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setPayout(payout) {
    this.payout = parseNumberByPrecision(payout, 1, userInfoStore.precision);
  }

  updatePayout(multiplier, amount) {
    this.payout = parseNumberByPrecision(multiplier * amount, 1, userInfoStore.precision);
  }

  setWinChance(winChance) {
    this.winChance = winChance;
    const newMultiplier = parseNumberPrecisionByDoteCount(
      100,
      userInfoStore.rtp / +winChance,
      DICE_MULTIPLIER_PRECISION
    );
    this.multiplier = Number.isFinite(newMultiplier) ? +newMultiplier : 0;
    this.detectRangeValueByOdd();
  }

  setMultiplier(odd) {
    this.multiplier = odd;
    this.setPayout(odd * userInfoStore.betAmount);
    const newWinChance = parseNumberPrecisionByDoteCount(
      100,
      userInfoStore.rtp / +odd,
      DICE_WIN_CHANCE_PRECISION
    );
    this.winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
    this.detectRangeValueByOdd();
  }

  detectRangeValueByOdd() {
    let rangeNumbersCount = getRangeNumberCountFromMultiplier[this.rangeType](
      this.multiplier,
      userInfoStore.rtp
    );
    if (rangeNumbersCount < this.diceRollLimitByBetType[this.rangeType].minBet) {
      rangeNumbersCount = this.diceRollLimitByBetType[this.rangeType].minBet;
    }
    if (rangeNumbersCount > this.diceRollLimitByBetType[this.rangeType].maxBet) {
      rangeNumbersCount = this.diceRollLimitByBetType[this.rangeType].maxBet;
    }
    this.rangeValue = rangeNumbersCount;
    const role = rangeNumbersCount;
    if (role > this.diceRollLimitByBetType[this.rangeType].maxBet) {
      this.roll = this.diceRollLimitByBetType[this.rangeType].maxBet;
    } else if (role < this.diceRollLimitByBetType[this.rangeType].minBet) {
      this.roll = this.diceRollLimitByBetType[this.rangeType].minBet;
    } else {
      this.roll = role;
    }
    this.setRangeCurrentValue(this.rangeValue);
    setTimeout(() => {
      this.setRangeCurrentValue(null);
    }, 200);
  }

  checkValidOdd(isBlur = false) {
    if (
      this.rangeType === RANGE_TYPE.Over &&
      !this.multiplier &&
      this.rangeValue === DICE_RANGE_LIMIT_OPTIONS.max
    ) {
      this.setRangeValue(this.rangeValue - 1);
    } else if (this.multiplier < this.multiplierLimit.minBet) {
      let sideByRangeType = this.rangeType === RANGE_TYPE.Under ? 'maxBet' : 'minBet';
      if (this.multiplier === null) {
        sideByRangeType = this.rangeType === RANGE_TYPE.Under ? 'minBet' : 'maxBet';
      }
      const validNumber = this.diceRollLimitByBetType[this.rangeType][sideByRangeType];
      this.setRangeValue(validNumber, isBlur);
      setTimeout(() => {
        this.setRangeCurrentValue(null);
      }, 200);
    }
  }

  setRangeType(rangeType) {
    this.rangeType = rangeType;
    this.detectRangeValueByOdd();
    if (userSettingsStore.isSoundOn) {
      soundService.play(DiceSoundsName.toggle, true);
    }
  }

  updateRangeType() {
    const rangeType = this.rangeType === RANGE_TYPE.Under ? RANGE_TYPE.Over : RANGE_TYPE.Under;
    this.setRangeType(rangeType);
  }

  setRangeValue(value, isBlur) {
    if (this.isBetsClosed) {
      return;
    }
    if (value !== this.rangeValue) {
      if (userSettingsStore.isSoundOn) {
        soundService.play(DiceSoundsName.toggle, true);
      }
    }
    this.rangeValue = value;
    this.roll = value;
    this.setRangeCurrentValue(value);
    this.countOddByRangeValue(value, isBlur);
  }

  updateRangeValueWithHotKeys(isIncrease) {
    const nextRangeValue = Math.floor(this.rangeValue + (isIncrease ? 1 : -1));
    const { maxBet: max, minBet: min } = this.diceRollLimitByBetType[this.rangeType];
    if (nextRangeValue > max || nextRangeValue < min) {
      return null;
    }
    this.setRangeValue(nextRangeValue);
    setTimeout(() => {
      this.setRangeCurrentValue(null);
    }, 200);
    return null;
  }

  countOddByRangeValue(value, isBlur) {
    const multiplier = getOddByBetType[this.rangeType](value, userInfoStore.rtp);
    this.multiplier = parseNumberByPrecision(multiplier, 1, DICE_MULTIPLIER_PRECISION);
    this.winChance = parseNumberByPrecision(
      100,
      +mathJS.format(userInfoStore.rtp / +multiplier, mathConfig),
      DICE_WIN_CHANCE_PRECISION
    );
    if (this.multiplier < this.multiplierLimit.minBet && isBlur) {
      this.setMultiplier(this.multiplierLimit.minBet);
    }
  }

  handleIncreaseMultiplier(isIncrease) {
    // let nextMultiplierValue =
    //   +this.multiplierWr + (isIncrease ? 1 : -1) * DICE_MULTIPLIER_STEP_VALUE;
    // if (nextMultiplierValue > this.multiplierLimit.maxBet) {
    //   this.setMultiplier(this.multiplierLimit.maxBet);
    // } else if (nextMultiplierValue < this.multiplierLimit.minBet) {
    //   this.setMultiplier(this.multiplierLimit.minBet);
    // } else {
    //   this.setMultiplier(parseNumberByPrecision(1, nextMultiplierValue, DICE_MULTIPLIER_PRECISION));
    // }
    //
    // this.countOddByRangeValue(this.roll);
    const valueForBetType = this.rangeType === RANGE_TYPE.Under ? 1 : -1;
    let newRangeValue =
      this.rangeValue + (isIncrease ? -1 : 1) * DICE_MULTIPLIER_STEP_VALUE * valueForBetType;
    if (newRangeValue > this.diceRollLimitByBetType[this.rangeType].maxBet) {
      newRangeValue = this.diceRollLimitByBetType[this.rangeType].maxBet;
    } else if (newRangeValue < this.diceRollLimitByBetType[this.rangeType].minBet) {
      newRangeValue = this.diceRollLimitByBetType[this.rangeType].minBet;
    }
    this.setRangeValue(newRangeValue);
    this.countOddByRangeValue(newRangeValue);
  }

  handleIncreaseWinChance(isIncrease) {
    const nextWinChanceValue = +this.winChance + (isIncrease ? 1 : -1) * DICE_WIN_CHANCE_STEP_VALUE;
    if (nextWinChanceValue > this.winChanceLimit.maxBet) {
      this.setWinChance(this.winChanceLimit.maxBet);
    } else if (nextWinChanceValue < this.winChanceLimit.minBet) {
      this.setWinChance(this.winChanceLimit.minBet);
    } else {
      this.setWinChance(parseNumberByPrecision(1, nextWinChanceValue, DICE_WIN_CHANCE_PRECISION));
    }
  }

  resetStore() {
    this.isBetsClosed = false;
    this.rangeValue = DEFAULT_RANGE_VALUE;
    this.rangeCurrentValue = null;
    this.rangeType = RANGE_TYPE.Under;
    this.multiplier = getOddByBetType[RANGE_TYPE.Under](DEFAULT_RANGE_VALUE, userInfoStore.rtp);
    this.winChance = parseNumberByPrecision(
      100,
      userInfoStore.rtp / +this.multiplier,
      DICE_WIN_CHANCE_PRECISION
    );
    this.winChanceLimit = {
      minBet: 1,
      maxBet: 96.029702,
    };
    this.multiplierLimit = {
      minBet: 1.0101,
      maxBet: 97,
    };
    this.payout = this.multiplier * userInfoStore.betAmount;
    this.roll = DEFAULT_RANGE_VALUE;
    this.gameResult = null;
    this.lastResults = [];
    this.maxWinLimit = null;
    this.isWin = null;
    this.isLastRoundWin = null;
    this.isShowResultLine = true;
    this.isAnimationEnd = false;
    this.diceRollLimitByBetType = {
      [RANGE_TYPE.Over]: {
        maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
          this.multiplierLimit.maxBet,
          userInfoStore.rtp
        ),
        minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Over](
          this.multiplierLimit.minBet,
          userInfoStore.rtp
        ),
      },
      [RANGE_TYPE.Under]: {
        maxBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
          this.multiplierLimit.minBet,
          userInfoStore.rtp
        ),
        minBet: getRangeNumberCountFromMultiplier[RANGE_TYPE.Under](
          this.multiplierLimit.maxBet,
          userInfoStore.rtp
        ),
      },
    };
    this.winAmount = null;
    this.winOdd = null;
  }
}

export const diceBetsStore = new DiceBetsStore();
